import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller, UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import InputField from '../InputField/InputField';
import Button from '../Button/Button';
import TextArea from '../TextArea/TextArea';
import Toggle from '../Toggle/Toggle';
import { Link } from 'react-router-dom';
import styles from './BranchAddEditModal.module.css';
import useNavigationPrompt from '../../hooks/useNavigationPrompt';
import { branchSchema, type BranchForm } from '../../schemas/branch.schema';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import {
    COUNTRY_SEED,
    findCountry,
    type SupportedCountryIso,
} from '../../lib/countries.seed';
import {
    getStatesForCountry,
    getStateLabel,
} from '../../lib/states.seed';
import { useBusinessDetailsQuery } from '../../hooks/useBusinessDetails';
import { translateValidationMessage } from '../../lib/ownerValidationMessages';

/**
 * CR 2026-05-10 — Country input swapped from a freeform `<InputField>` to the
 * signup `<CountrySelect>` (search-driven Headless UI Combobox backed by the
 * same `COUNTRY_SEED` master). To preserve the existing data shape on the BE
 * (legacy `BusinessLocation.Country` rows hold the EN country name as a
 * string), we keep the form's external contract on the EN name and convert
 * to / from ISO-3166-1 alpha-2 internally for the picker.
 */
function countryStringToIso(value: string | undefined): SupportedCountryIso {
    if (!value) return 'AE';
    const trimmed = value.trim();
    // Already an ISO2?
    const upper = trimmed.toUpperCase();
    if (upper.length === 2) {
        const direct = findCountry(upper as SupportedCountryIso);
        if (direct) return direct.iso2;
    }
    // Resolve by EN or AR name (legacy records).
    const lower = trimmed.toLowerCase();
    const found = COUNTRY_SEED.find(
        (c) => c.nameEn.toLowerCase() === lower || c.nameAr === trimmed,
    );
    return found?.iso2 ?? 'AE';
}

function isoToCountryName(iso: string): string {
    const c = findCountry(iso as SupportedCountryIso);
    return c?.nameEn ?? iso;
}

// Leaflet default marker icon: CDN URLs avoid bundler issues with static assets
const DefaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Dubai coordinates — sensible fallback for UAE-centric app
const DEFAULT_POSITION: [number, number] = [25.2048, 55.2708];

/** Applies a Nominatim address object's fields into the RHF form. */
function applyGeocodedAddress(
    addr: Record<string, string>,
    setValue: UseFormSetValue<BranchForm>
) {
    const streetAddress = [addr.house_number, addr.road].filter(Boolean).join(' ');
    if (streetAddress) setValue('StreetAddress', streetAddress, { shouldDirty: true });
    const city = addr.city || addr.town || addr.village;
    if (city) setValue('City', city, { shouldDirty: true });
    if (addr.state) setValue('State', addr.state, { shouldDirty: true });
    if (addr.country) setValue('Country', addr.country, { shouldDirty: true });
}

// Keep original shape for external consumers
export interface BranchFormData {
    Branch: string;
    /** O3 (SO-24) — optional Arabic branch name. */
    BranchNameAr?: string;
    StreetAddress: string;
    City: string;
    State: string;
    Country: string;
    PhoneNumber: string;
    Latitude?: number;
    Longitude?: number;
    Status: boolean;
    Headquarters: boolean;
    Description: string;
}

interface BranchAddEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData: BranchFormData;
    onSave: (branch: BranchFormData) => void;
    isAdd?: boolean;
    onDeleteClicked: () => void;
    loading?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
}

interface LocationMarkerProps {
    position: [number, number];
    onPositionChange: (lat: number, lng: number) => void;
}

/**
 * CR-2026-05-11 — Marker is now draggable AND the map still accepts
 * click-to-set as a fallback. Drag-end fires the same setter as click,
 * so the existing reverse-geocode pipeline (Street/City/State auto-fill)
 * keeps working when the owner drags to a new spot.
 */
const LocationMarker: React.FC<LocationMarkerProps> = ({ position, onPositionChange }) => {
    useMapEvents({
        click(e) {
            onPositionChange(e.latlng.lat, e.latlng.lng);
        },
    });
    return position ? (
        <Marker
            position={position}
            draggable
            eventHandlers={{
                dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    onPositionChange(lat, lng);
                },
            }}
        />
    ) : null;
};

interface MapCenterProps {
    position: [number, number];
}

const MapCenter: React.FC<MapCenterProps> = ({ position }) => {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.setView(position, map.getZoom());
        }
    }, [position, map]);
    return null;
};

const BranchAddEditModal: React.FC<BranchAddEditModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onSave,
    onDeleteClicked,
    isAdd,
    loading = false,
    onDirtyChange
}) => {
    const { language, t } = useLanguage();
    const locale: 'en' | 'ar' = language === 'ar' ? 'ar' : 'en';
    // O3 (SO-38) — schema messages are English; show the localized copy.
    const errText = (m: string | undefined) => translateValidationMessage(m, t);

    // CR-2026-05-10 Phase 1 — Country lives on Business, not on Branch.
    // Read it once and inherit; the modal no longer offers a picker.
    const { data: business } = useBusinessDetailsQuery();
    const businessCountryRaw = (business?.location as { country?: string } | undefined)?.country;
    const businessCountryIso = countryStringToIso(businessCountryRaw);
    const businessCountrySeed = findCountry(businessCountryIso);
    const businessCountryName = businessCountrySeed?.nameEn ?? '';

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isDirty },
    } = useForm<BranchForm>({
        resolver: zodResolver(branchSchema),
        defaultValues: {
            Branch: initialData?.Branch ?? '',
            BranchNameAr: initialData?.BranchNameAr ?? '',
            StreetAddress: initialData?.StreetAddress ?? '',
            City: initialData?.City ?? '',
            State: initialData?.State ?? '',
            // CR-2026-05-10 — Country always inherits from Business; the
            // form value is the Business country at open time. On save
            // we convert ISO-2 → EN name. Existing branch records that
            // have a different / drifted country get healed to the
            // business country on next save.
            Country: businessCountryIso,
            PhoneNumber: initialData?.PhoneNumber ?? '',
            Latitude: initialData?.Latitude,
            Longitude: initialData?.Longitude,
            Status: initialData?.Status ?? true,
            Headquarters: initialData?.Headquarters ?? false,
            Description: initialData?.Description ?? '',
        },
    });

    const watchedLat = watch('Latitude');
    const watchedLng = watch('Longitude');
    const watchedCountry = watch('Country') as string | undefined;
    const watchedState = watch('State') as string | undefined;

    // CR 2026-05-10 — When the country switches to one with a curated
    // state list, validate that the current State is a member; otherwise
    // clear it so the user explicitly re-picks. Avoids saving e.g.
    // "Dubai" as a Jordan governorate after a country swap.
    useEffect(() => {
        const states = getStatesForCountry(watchedCountry);
        if (!states) return;
        if (!watchedState) return;
        const isMember = states.some(
            (s) => s.nameEn === watchedState || s.nameAr === watchedState,
        );
        if (!isMember) {
            setValue('State', '', { shouldDirty: true });
        }
    }, [watchedCountry, watchedState, setValue]);

    const markerPosition: [number, number] = useMemo(
        () => (watchedLat && watchedLng ? [watchedLat, watchedLng] : DEFAULT_POSITION),
        [watchedLat, watchedLng]
    );

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isGeolocating, setIsGeolocating] = useState(false);
    const { showToast } = useToast();

    // Notify parent of dirty state
    useEffect(() => {
        if (onDirtyChange) onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    // Reset form when modal opens with fresh initialData
    useEffect(() => {
        if (!isOpen) return;
        reset({
            Branch: initialData?.Branch ?? '',
            BranchNameAr: initialData?.BranchNameAr ?? '',
            StreetAddress: initialData?.StreetAddress ?? '',
            City: initialData?.City ?? '',
            State: initialData?.State ?? '',
            // Always inherit Country from Business (see open-time comment).
            Country: businessCountryIso,
            PhoneNumber: initialData?.PhoneNumber ?? '',
            Latitude: initialData?.Latitude,
            Longitude: initialData?.Longitude,
            Status: initialData?.Status ?? true,
            Headquarters: initialData?.Headquarters ?? false,
            Description: initialData?.Description ?? '',
        });
        setSearchQuery('');
        if (onDirtyChange) onDirtyChange(false);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePositionChange = useCallback(async (lat: number, lng: number) => {
        setValue('Latitude', lat, { shouldDirty: true });
        setValue('Longitude', lng, { shouldDirty: true });
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const result = await response.json();
            if (result?.address) applyGeocodedAddress(result.address, setValue);
        } catch (error) {
            console.error('Reverse geocoding failed:', error);
        }
    }, [setValue]);

    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&addressdetails=1&limit=1`
            );
            const results = await response.json();
            if (results?.length > 0) {
                const result = results[0];
                setValue('Latitude', parseFloat(result.lat), { shouldDirty: true });
                setValue('Longitude', parseFloat(result.lon), { shouldDirty: true });
                if (result.address) applyGeocodedAddress(result.address, setValue);
            }
        } catch (error) {
            console.error('Address search failed:', error);
        } finally {
            setIsSearching(false);
        }
    }, [searchQuery, setValue]);

    /**
     * CR-2026-05-11 — One-shot browser geolocation. On accept, drops the
     * pin at the user's current coords AND fires the existing
     * reverse-geocode pipeline (Street/City/State auto-fill) so the
     * address fields populate too. Toast guides the user when permission
     * is denied or the API isn't available.
     */
    const handleGeolocate = useCallback(() => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            showToast(t('mb.branchForm.geo.unsupported'), 'error');
            return;
        }
        setIsGeolocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                handlePositionChange(pos.coords.latitude, pos.coords.longitude);
                setIsGeolocating(false);
            },
            (err) => {
                const msg = err.code === err.PERMISSION_DENIED
                    ? t('mb.branchForm.geo.denied')
                    : t('mb.branchForm.geo.failed');
                showToast(msg, 'error');
                setIsGeolocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    }, [handlePositionChange, showToast, t]);

    const onValid = (data: BranchForm) => {
        // Convert the picker's ISO-2 back to the EN country name before
        // emitting upstream — the BE column + the legacy `/branches` table
        // and the `BranchesSection` card list all read this as a full name.
        const externalData: BranchFormData = {
            ...(data as BranchFormData),
            Country: isoToCountryName(data.Country),
        };
        onSave(externalData);
        reset(data); // clears isDirty (form keeps ISO-2 internally)
        if (onDirtyChange) onDirtyChange(false);
    };

    const handleClose = () => {
        if (isDirty) {
            const confirmClose = window.confirm(t('mb.common.discardChanges'));
            if (!confirmClose) return;
        }
        onClose();
    };

    // Prompt on browser/tab close when unsaved changes exist
    useEffect(() => {
        const beforeUnload = (e: BeforeUnloadEvent) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        if (isOpen && isDirty) {
            window.addEventListener('beforeunload', beforeUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
        };
    }, [isOpen, isDirty]);

    useNavigationPrompt(isOpen && isDirty, t('mb.common.discardChanges'));

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSearch();
    };

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContent}>
                            <div className={styles.spinner}></div>
                            <span>{t('mb.branchForm.saving')}</span>
                        </div>
                    </div>
                )}

                <div className={`${styles.headerCloseInputsContainer} ${styles.darkInputs}`}>
                    <button type="button" className={styles.closeButton} onClick={handleClose} disabled={loading} aria-label={t('mb.common.close')}>
                        &times;
                    </button>
                    <h2 className={`${styles.header} headerText`}>
                        {isAdd ? t('mb.branchForm.addTitle') : t('mb.branchForm.editTitle')}
                    </h2>

                    <Controller
                        name="Branch"
                        control={control}
                        render={({ field }) => (
                            <InputField
                                label={t('mb.branchForm.name')}
                                name="Branch"
                                value={field.value}
                                onChange={(val) => field.onChange(val.replace(/[^A-Za-z0-9 ]/g, ''))}
                                placeholder={t('mb.branchForm.namePlaceholder')}
                                disabled={loading}
                                feedback={errors.Branch ? 'error' : undefined}
                                feedbackMessage={errText(errors.Branch?.message)}
                                required
                            />
                        )}
                    />

                    <Controller
                        name="BranchNameAr"
                        control={control}
                        render={({ field }) => (
                            <InputField
                                label={t('mb.branchForm.nameAr')}
                                name="BranchNameAr"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                placeholder={t('mb.branchForm.nameArPlaceholder')}
                                disabled={loading}
                                dir="rtl"
                                maxLength={100}
                                feedback={errors.BranchNameAr ? 'error' : undefined}
                                feedbackMessage={errText(errors.BranchNameAr?.message)}
                            />
                        )}
                    />
                    <p className={styles.mapHint}>{t('mb.branchForm.nameArHint')}</p>

                    <div className={styles.mapSection}>
                        <label className={styles.sectionLabel}>{t('mb.branchForm.location')}</label>
                        <p className={styles.mapHint}>
                            {t('mb.branchForm.locationHint')}
                        </p>
                        <div className={styles.searchContainer}>
                            <input
                                type="text"
                                className={styles.mapSearchInput}
                                placeholder={t('mb.branchForm.searchPlaceholder')}
                                aria-label={t('mb.branchForm.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading || isSearching || isGeolocating}
                            />
                            <button
                                className={styles.searchButton}
                                onClick={handleSearch}
                                type="button"
                                disabled={loading || isSearching || !searchQuery.trim()}
                                aria-label={t('mb.branchForm.searchAria')}
                            >
                                {isSearching ? (
                                    <div className={styles.searchSpinner}></div>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>
                            {/* CR-2026-05-11 — One-shot geolocation. Browser
                                permission prompt fires on first click; on
                                accept the pin jumps to the user's coords +
                                reverse-geocodes the address fields. */}
                            <button
                                className={styles.geolocateButton}
                                onClick={handleGeolocate}
                                type="button"
                                disabled={loading || isGeolocating}
                                aria-label={t('mb.branchForm.useLocation')}
                                title={t('mb.branchForm.useLocation')}
                            >
                                {isGeolocating ? (
                                    <div className={styles.searchSpinner}></div>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" />
                                        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                                    </svg>
                                )}
                                <span className={styles.geolocateButtonLabel}>{t('mb.branchForm.myLocation')}</span>
                            </button>
                        </div>
                        <div className={styles.mapContainer}>
                            <MapContainer
                                center={markerPosition}
                                zoom={15}
                                style={{ height: '100%', width: '100%' }}
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <LocationMarker
                                    position={markerPosition}
                                    onPositionChange={handlePositionChange}
                                />
                                <MapCenter position={markerPosition} />
                            </MapContainer>
                        </div>
                        {/* CR-2026-05-11 — Live coordinate readout below the
                            map. Confirms what the owner is about to save and
                            doubles as a quick paste-source for support. */}
                        {watchedLat != null && watchedLng != null && (
                            <div className={styles.coordReadout}>
                                <span className={styles.coordReadoutIcon} aria-hidden="true">📍</span>
                                <span className={styles.coordReadoutValue} dir="ltr">
                                    {Number(watchedLat).toFixed(6)}, {Number(watchedLng).toFixed(6)}
                                </span>
                                <span className={styles.coordReadoutHint}>
                                    {t('mb.branchForm.dragPin')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={styles.addressSection}>
                        <label className={styles.sectionLabel}>{t('mb.branchForm.address')}</label>

                        {/* CR 2026-05-10 — Field order changed per founder
                            request: Country → City + State → Street Address.
                            Top-down geographic specificity (country picks
                            the State list; State narrows the City; Street
                            Address is the most specific and last). */}

                        {/* CR-2026-05-10 Phase 1 — Country is read-only;
                            it lives on the Business and is shared across
                            all branches. To change it, the owner edits
                            Business Details. */}
                        <div className={styles.countryReadOnly}>
                            <div className={styles.countryReadOnlyLabel}>{t('mb.branchForm.country')}</div>
                            <div className={styles.countryReadOnlyValue}>
                                {businessCountrySeed && (
                                    <span
                                        className={`fi fi-${businessCountrySeed.iso2Lower} ${styles.countryReadOnlyFlag}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span>
                                    {locale === 'ar' && businessCountrySeed
                                        ? businessCountrySeed.nameAr
                                        : businessCountryName || '—'}
                                </span>
                                <Link
                                    to="/my-business/business-details"
                                    className={styles.countryReadOnlyLink}
                                    onClick={(e) => {
                                        if (isDirty) {
                                            const ok = window.confirm(
                                                t('mb.branchForm.discardAndGo'),
                                            );
                                            if (!ok) e.preventDefault();
                                        }
                                    }}
                                >
                                    {t('mb.branchForm.editInDetails')}
                                </Link>
                            </div>
                            <div className={styles.countryReadOnlyHint}>
                                {t('mb.branchForm.countryHint')}
                            </div>
                        </div>

                        <div className={styles.addressRow}>
                            <Controller
                                name="City"
                                control={control}
                                render={({ field }) => (
                                    <InputField
                                        label={t('mb.branchForm.city')}
                                        name="City"
                                        value={field.value}
                                        onChange={(val) => field.onChange(val.replace(/[^A-Za-z \-']/g, ''))}
                                        placeholder={t('mb.branchForm.city')}
                                        disabled={loading}
                                        feedback={errors.City ? 'error' : undefined}
                                        feedbackMessage={errText(errors.City?.message)}
                                        required
                                    />
                                )}
                            />
                            <Controller
                                name="State"
                                control={control}
                                render={({ field }) => {
                                    const watchedCountry = watch('Country') as string;
                                    const states = getStatesForCountry(watchedCountry);
                                    // O3 — state label localized (getStateLabel is English-only).
                                    const stateLabelKey: Record<string, string> = {
                                        'Emirate (optional)': 'mb.branchForm.state.emirate',
                                        'Governorate (optional)': 'mb.branchForm.state.governorate',
                                        'Region (optional)': 'mb.branchForm.state.region',
                                    };
                                    const rawLabel = getStateLabel(watchedCountry);
                                    const label = t(stateLabelKey[rawLabel] ?? 'mb.branchForm.state.generic');

                                    if (states && states.length > 0) {
                                        // Country has a curated list — render
                                        // the dark <select>. Stored value is
                                        // the EN name (matches BE shape).
                                        return (
                                            <div className={styles.stateField}>
                                                <label htmlFor="branch-state-select">{label}</label>
                                                <select
                                                    id="branch-state-select"
                                                    name="State"
                                                    className={styles.stateSelect}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    disabled={loading}
                                                    aria-invalid={errors.State ? true : undefined}
                                                >
                                                    <option value="">{t('mb.branchForm.selectPlaceholder')}</option>
                                                    {states.map((s) => (
                                                        <option key={s.code} value={s.nameEn}>
                                                            {locale === 'ar' ? s.nameAr : s.nameEn}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    }

                                    return (
                                        <InputField
                                            label={label}
                                            name="State"
                                            value={field.value ?? ''}
                                            onChange={(val) => field.onChange(val.replace(/[^A-Za-z \-']/g, ''))}
                                            placeholder={t('mb.common.optionalWord')}
                                            disabled={loading}
                                            feedback={errors.State ? 'error' : undefined}
                                            feedbackMessage={errText(errors.State?.message)}
                                        />
                                    );
                                }}
                            />
                        </div>

                        <Controller
                            name="StreetAddress"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    label={t('mb.branchForm.street')}
                                    name="StreetAddress"
                                    value={field.value}
                                    onChange={(val) => field.onChange(val.replace(/[^A-Za-z0-9 \-#.,/'"]/g, ''))}
                                    placeholder={t('mb.branchForm.streetPlaceholder')}
                                    disabled={loading}
                                    feedback={errors.StreetAddress ? 'error' : undefined}
                                    feedbackMessage={errText(errors.StreetAddress?.message)}
                                    required
                                />
                            )}
                        />
                    </div>

                    <div className={styles.phoneSection}>
                        <Controller
                            name="PhoneNumber"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    label={t('mb.branchForm.phone')}
                                    name="PhoneNumber"
                                    type="tel"
                                    value={field.value}
                                    onChange={(val) => field.onChange(val.replace(/[^0-9+]/g, ''))}
                                    placeholder="+1234567890"
                                    disabled={loading}
                                    feedback={errors.PhoneNumber ? 'error' : undefined}
                                    feedbackMessage={errText(errors.PhoneNumber?.message)}
                                    required
                                    maxLength={15}
                                />
                            )}
                        />
                        <p className={styles.phoneHint}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="12" cy="8" r="1" fill="currentColor" />
                            </svg>
                            {t('mb.branchForm.phoneHint')}
                        </p>
                    </div>

                    {/* CR 2026-05-10 — Branch settings card. Groups the
                        previously-loose Active toggle + HQ Y/N buttons +
                        Description block into one cohesive section that
                        matches the BusinessDetailsSection card pattern.
                        HQ converted from Yes/No buttons to a single
                        toggle so it visually matches Active. */}
                    <div className={styles.settingsCard}>
                        <div className={styles.settingsCardTitle}>{t('mb.branchForm.settings')}</div>
                        <div className={styles.settingsCardSubtitle}>
                            {t('mb.branchForm.settingsSub')}
                        </div>

                        <div className={styles.settingsRow}>
                            <div className={styles.settingsRowText}>
                                <div className={styles.settingsRowLabel}>{t('mb.common.active')}</div>
                                <div className={styles.settingsRowHelp}>{t('mb.branchForm.activeHelp')}</div>
                            </div>
                            <div className={styles.settingsRowControl}>
                                <Controller
                                    name="Status"
                                    control={control}
                                    render={({ field }) => (
                                        <Toggle
                                            checked={field.value}
                                            onChange={field.onChange}
                                            disabled={loading}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className={styles.settingsRow}>
                            <div className={styles.settingsRowText}>
                                <div className={styles.settingsRowLabel}>{t('mb.branchForm.hq')}</div>
                                <div className={styles.settingsRowHelp}>{t('mb.branchForm.hqHelp')}</div>
                            </div>
                            <div className={styles.settingsRowControl}>
                                <Controller
                                    name="Headquarters"
                                    control={control}
                                    render={({ field }) => (
                                        <Toggle
                                            checked={field.value}
                                            onChange={field.onChange}
                                            disabled={loading}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className={styles.settingsBlock}>
                            <div className={styles.settingsBlockHeader}>
                                <div className={styles.settingsRowLabel}>{t('mb.branchForm.description')}</div>
                                <div className={styles.settingsRowHelp}>{t('mb.branchForm.descriptionHelp')}</div>
                            </div>
                            <Controller
                                name="Description"
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        name="Description"
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                        placeholder={t('mb.branchForm.descriptionPlaceholder')}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.buttonContainer}>
                    <div className={styles.deleteButton}>
                        {!isAdd && (
                            <Button
                                label={t('mb.branches.delete')}
                                onClick={onDeleteClicked}
                                size="small"
                                disabled={loading}
                                icon={
                                    <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M13.7637 4.67346L12.938 17.6342C12.938 18.3814 12.3523 18.9926 11.638 18.9926H4.55157C3.838 18.9926 3.25443 18.3814 3.25443 17.6342L2.42871 4.67346" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M4.57129 4.21502L4.642 2.11233C4.642 1.50106 5.03986 1 5.52486 1H10.3406C10.8263 1 11.2227 1.50106 11.2227 2.11233L11.2927 4.21502" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M1 4.30613H15.8671" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M5.38863 7.61224L5.89792 16.5167" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M7.64371 10.551L7.928 16.4851" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                                iconPosition="left"
                                noAppearance={true}
                                backgroundColor="transparent"
                                fontColor="var(--destructive-fg)"
                            />
                        )}
                    </div>
                    <div className={styles.cancelAddSave}>
                        <Button
                            label={t('mb.common.cancel')}
                            onClick={handleClose}
                            noAppearance={true}
                            size="small"
                            disabled={loading}
                        />
                        <Button
                            label={isAdd ? t('mb.common.add') : t('mb.common.save')}
                            onClick={handleSubmit(onValid)}
                            size="small"
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BranchAddEditModal;
